import { useMemo } from 'react';
import {
    useTradeLogicRegistryFeatures,
    useTradeLogicRegistryTemplates,
    type TradeLogicRegistryFeatureDto
} from '@/features/tradeLogicAuthoring/registry';
import {
    collectRuleTreeFeatureKeys,
    describeImportedConditionExpression,
    readImportedConditionExpression
} from './importedExpressionFormat';
import { formatLogicNodeSummary, type NodeSummaryFormatContext } from './nodeSummaryFormat';
import { isImportedRawConditionNode, isLogicContainerNode, type LogicNode } from '../model';

const INDENT_PX = 12;

const ReadOnlyTreeRow = ({ node, depth, summaryContext }: { node: LogicNode; depth: number; summaryContext: NodeSummaryFormatContext }) => {
    const summary = formatLogicNodeSummary(node, summaryContext);
    const indent = { paddingLeft: `${depth * INDENT_PX}px` };
    const importedLines = isImportedRawConditionNode(node)
        ? describeImportedConditionExpression(readImportedConditionExpression(node), (featureKey) => {
              const feature = summaryContext.featuresByKey[featureKey];

              return feature?.displayLabel ?? feature?.label ?? featureKey;
          })
        : [];
    const hasChildren = isLogicContainerNode(node) && node.children.length > 0;
    const muteColor = depth === 0 ? 'text-zinc-300' : 'text-zinc-200';

    return (
        <div>
            <div className={`flex items-center gap-2 ${muteColor}`} style={indent}>
                <span className="text-xs uppercase text-zinc-500">{node.kind}</span>
                <span className="text-sm">{summary}</span>
            </div>
            {importedLines.length > 0 && (
                <div className="mt-1 space-y-0.5 font-mono text-xs text-zinc-400">
                    {importedLines.map((line, index) => (
                        <div key={index} style={{ paddingLeft: `${(depth + 1 + line.depth) * INDENT_PX}px` }}>
                            {line.text}
                        </div>
                    ))}
                </div>
            )}
            {hasChildren && isLogicContainerNode(node) && (
                <div className="mt-1 space-y-1">
                    {node.children.map((child) => (
                        <ReadOnlyTreeRow depth={depth + 1} key={child.id} node={child} summaryContext={summaryContext} />
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * A rule tree as readable text, one indented line per node, labelled with the registry's feature
 * and template names; imported raw conditions also spell out their expression. Read-only; editing
 * happens in the rule editor.
 */
const TradeLogicRuleTree = ({ rootNode }: { rootNode: LogicNode }) => {
    // Ask for exactly the features this tree reads: context features used by imported rules are
    // often outside the authoring list, and they still deserve their registry names.
    const featureKeys = useMemo(() => collectRuleTreeFeatureKeys(rootNode), [rootNode]);
    const featuresQuery = useTradeLogicRegistryFeatures({ featureKeys }, { enabled: featureKeys.length > 0 });
    const featuresByKey = useMemo<Record<string, TradeLogicRegistryFeatureDto>>(
        () => Object.fromEntries(featuresQuery.data.map((feature) => [feature.featureKey, feature])),
        [featuresQuery.data]
    );
    const templatesQuery = useTradeLogicRegistryTemplates({ enabled: true });
    const summaryContext: NodeSummaryFormatContext = {
        actionTemplatesByKey: templatesQuery.actionTemplatesByKey,
        conditionTemplatesByKey: templatesQuery.conditionTemplatesByKey,
        featuresByKey
    };

    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-950/40 p-3">
            <ReadOnlyTreeRow depth={0} node={rootNode} summaryContext={summaryContext} />
        </div>
    );
};

export default TradeLogicRuleTree;
